import React, {useContext, useMemo} from 'react';

import {
	WebviewBridgeEmitter,
	AndroidSender,
	IOSSender,
	WebSender,
} from '@deezer/webview-bridge';
import {useURLParams} from './URLParamsProvider';
import {MobileOS} from './types';

type BridgeProviderValue = WebviewBridgeEmitter | false;

export const BridgeContext = React.createContext<BridgeProviderValue | void>(
	undefined,
);

const BridgeProvider: React.FC<React.PropsWithChildren<unknown>> = ({
	children,
}) => {
	const {platform} = useURLParams();
	const value = useMemo(() => {
		let sender = null;

		switch (platform) {
			case MobileOS.ANDROID:
				sender = new AndroidSender();
				break;
			case MobileOS.IOS:
				sender = new IOSSender();
				break;
			case MobileOS.NULL:
			default:
				if (typeof window !== 'undefined' && window.self === window.top) {
					// We're running on web, not in an iframe
					return false;
				}
				sender = new WebSender();
				break;
		}

		return new WebviewBridgeEmitter(sender);
	}, [platform]);

	return (
		<BridgeContext.Provider value={value}>{children}</BridgeContext.Provider>
	);
};

export default BridgeProvider;

export const useBridge = (): BridgeProviderValue => {
	const bridgeContext = useContext(BridgeContext);

	if (bridgeContext === false) {
		return false;
	}

	if (!bridgeContext) {
		throw new Error('useBridge must be used whithin a BridgeProvider');
	}

	return bridgeContext;
};
